import { useEffect, useRef, useState } from 'react';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import './Hero.css';

// Import local assets
import heroBg from '../assets/spices5.jpg';

const spiceWords = ['Hand-Ground', 'Pure Saffron', 'Stone-Pressed', 'Authentic', 'Sun-Dried'];

const Hero = () => {
  const containerRef = useRef(null);
  const [wordIndex, setWordIndex] = useState(0);
  const [mousePos, setMousePos] = useState({ x: 0.5, y: 0.5 });

  const { scrollYProgress } = useScroll({ target: containerRef });
  const y = useTransform(scrollYProgress, [0, 1], ['0%', '50%']);
  const textY = useTransform(scrollYProgress, [0, 1], ['0%', '-20%']);
  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.2]);

  useEffect(() => {
    const interval = setInterval(() => {
      setWordIndex((prev) => (prev + 1) % spiceWords.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleMouse = (e) => {
      setMousePos({
        x: e.clientX / window.innerWidth,
        y: e.clientY / window.innerHeight,
      });
    };
    window.addEventListener('mousemove', handleMouse);
    return () => window.removeEventListener('mousemove', handleMouse);
  }, []);

  return (
    <section className="hero" ref={containerRef}>
      {/* Cinematic Background Layer */}
      <motion.div
        className="hero-bg-layer"
        style={{
          y,
          scale,
          backgroundPosition: `${50 + (mousePos.x - 0.5) * 5}% ${50 + (mousePos.y - 0.5) * 5}%`,
          backgroundImage: `url(${heroBg})`
        }}
      />
      
      {/* Floating Elements / Particles */}
      <div className="hero-floating-elements">
        <motion.div 
          className="floating-leaf leaf-1"
          animate={{ y: [0, -20, 0], rotate: [0, 10, 0] }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div 
          className="floating-leaf leaf-2"
          animate={{ y: [0, 20, 0], rotate: [0, -10, 0] }}
          transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
        />
      </div>

      <div className="hero-overlay-main" />
      <div className="hero-vignette" />

      {/* Main Content */}
      <motion.div className="hero-content-wrapper" style={{ opacity, y: textY }}>
        <motion.div
          className="hero-tag-new"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <span>Tradition in Every Pinch</span>
        </motion.div>

        <div className="hero-title-main">
          <motion.h1
            initial={{ opacity: 0, y: 100 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, ease: [0.19, 1, 0.22, 1] }}
          >
            Taste the <span className="italic-serif">Soul</span> of <br />
            <span className="gradient-text">Authentic India</span>
          </motion.h1>
          
          <div className="hero-word-rotator">
            <AnimatePresence mode="wait">
              <motion.span
                key={wordIndex}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ duration: 0.5 }}
                className="rotating-word"
              >
                {spiceWords[wordIndex]}
              </motion.span>
            </AnimatePresence>
            <span className="static-suffix">Pure Masale</span>
          </div>
        </div>

        <motion.p
          className="hero-description"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8, duration: 1 }}
        >
          Crafted in the kitchens of tradition, ground on stones of history. <br />
          Experience flavors that connect you back to your roots.
        </motion.p>

        <motion.div
          className="hero-actions-new"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.8 }}
        >
          <a href="/products" className="btn-primary-glow" data-cursor>
            View Collection
          </a>
          <a href="/story" className="btn-play-story" data-cursor>
            <span className="play-icon">▶</span>
            Explore Our Story
          </a>
        </motion.div>
      </motion.div>

      {/* Bottom Features Bar */}
      <motion.div 
        className="hero-features-bar"
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.5, duration: 1 }}
      >
        <div className="feature-item">
          <strong>100%</strong>
          <span>Pure & Natural</span>
        </div>
        <div className="feature-divider" />
        <div className="feature-item">
          <strong>Hand</strong>
          <span>Stone-Ground</span>
        </div>
        <div className="feature-divider" />
        <div className="feature-item">
          <strong>Direct</strong>
          <span>Farmer Sourcing</span>
        </div>
      </motion.div>

      {/* Animated Scroll Line */}
      <div className="hero-scroll-indicator">
        <motion.div 
          className="scroll-progress-line"
          animate={{ height: ['0%', '100%', '0%'] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
        />
      </div>
    </section>
  );
};

export default Hero;
